import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { getDeliverables, createDeliverable, updateDeliverable, deleteDeliverable, getWorkPackages, uploadResourceFile } from '../../services/apiService';

const Deliverables = () => {
  const [userEmail, setUserEmail] = useState('');
  const [deliverables, setDeliverables] = useState([]);
  const [workPackages, setWorkPackages] = useState([]);
  const [workPackageIdsFromDeliverables, setWorkPackageIdsFromDeliverables] = useState([]);
  const [editingDeliverable, setEditingDeliverable] = useState(null);
  const [showDeliverableForm, setShowDeliverableForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [searchTerm, setSearchTerm] = useState('');
  const [wpFilter, setWpFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deliverableToDelete, setDeliverableToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const email = localStorage.getItem('userEmail');
    
    if (!isLoggedIn || !email) {
      navigate('/login');
      return;
    }
    
    setUserEmail(email);
    fetchDeliverables();
    fetchWorkPackages();
  }, [navigate]);

  // Charger les work packages une seule fois au montage
  // workPackages contient TOUJOURS tous les work packages, indépendamment des filtres
  // Pas besoin de recharger quand deliverables.length change

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 4000);
  };

  const fetchWorkPackages = async () => {
    try {
      // Récupérer TOUS les work packages (indépendamment des filtres ou des deliverables)
      const allWorkPackages = await getWorkPackages();
      
      // S'assurer que chaque work package a bien un id et un title
      const validWorkPackages = allWorkPackages.filter(wp => wp.id && wp.title);
      setWorkPackages(validWorkPackages);
      
      console.log('Tous les work packages chargés pour le select:', validWorkPackages.length);
      
      // Extraire les wp uniques depuis TOUS les deliverables (sans filtre) pour le filtre de recherche uniquement
      const allDeliverables = await getDeliverables();
      const uniqueWpIds = [...new Set(allDeliverables
        .map(del => del.wp)
        .filter(wp => wp && wp.trim() !== ''))];
      setWorkPackageIdsFromDeliverables(uniqueWpIds);
    } catch (error) {
      console.error('Error loading work packages:', error);
      setWorkPackages([]);
    }
  };

  const fetchDeliverables = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filters = {};
      if (wpFilter) filters.wp = wpFilter;
      if (statusFilter) filters.status = statusFilter;
      
      const data = await getDeliverables(filters);
      const transformed = data.map(del => ({
        ...del,
        id: del.id || del.mongoId,
        mongoId: del.mongoId || del._id
      }));
      setDeliverables(transformed);
      
      // Note: Ne pas mettre à jour workPackageIdsFromDeliverables ici car cela dépendrait des filtres
      // workPackageIdsFromDeliverables est mis à jour dans fetchWorkPackages avec TOUS les deliverables (sans filtre)
    } catch (err) {
      setError('Error loading deliverables');
      setDeliverables([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliverables();
  }, [wpFilter, statusFilter]);

  const handleDeliverableSubmit = async (deliverableData) => {
    try {
      const apiData = {
        id: deliverableData.id?.trim() || '',
        wp: deliverableData.wp?.trim() || '',
        description: deliverableData.description?.trim() || '',
        status: deliverableData.status || 'pending',
        file: deliverableData.file || null,
        date: deliverableData.date?.trim() || '',
        lead: deliverableData.lead?.trim() || ''
      };

      if (editingDeliverable) {
        const updated = await updateDeliverable(editingDeliverable.mongoId || editingDeliverable.id, apiData);
        if (updated) {
          await fetchDeliverables();
          setShowDeliverableForm(false);
          setEditingDeliverable(null);
          showNotification('Deliverable updated successfully', 'success');
        }
      } else {
        const created = await createDeliverable(apiData);
        if (created) {
          await fetchDeliverables();
          setShowDeliverableForm(false);
          showNotification('Deliverable created successfully', 'success');
        }
      }
    } catch (error) {
      showNotification('Error saving: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  const handleDeleteDeliverable = (id) => {
    const item = deliverables.find(del => (del.id === id || del.mongoId === id));
    if (item) {
      setDeliverableToDelete(item);
    }
  };

  const confirmDeleteDeliverable = async () => {
    if (deliverableToDelete) {
      try {
        const success = await deleteDeliverable(deliverableToDelete.mongoId || deliverableToDelete.id);
        if (success) {
          await fetchDeliverables();
          setDeliverableToDelete(null);
          showNotification('Deliverable deleted successfully', 'success');
        }
      } catch (error) {
        showNotification('Error deleting: ' + (error.message || 'Unknown error'), 'error');
      }
    }
  };

  const filteredDeliverables = deliverables.filter(del => {
    const matchesSearch = !searchTerm || 
      (del.id && del.id.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (del.description && del.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDeliverables = filteredDeliverables.slice(startIndex, endIndex);
  const totalPages = Math.max(1, Math.ceil(filteredDeliverables.length / itemsPerPage));

  if (isLoading) {
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '24px', color: '#96ca2d', marginBottom: '20px' }}>
            <i className="fas fa-spinner fa-spin" />
          </div>
          <p style={{ fontSize: '16px', color: '#666' }}>Loading deliverables...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '24px', color: '#ef4444', marginBottom: '20px' }}>
            <i className="fas fa-exclamation-triangle" />
          </div>
          <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>{error}</p>
          <button
            onClick={fetchDeliverables}
            style={{
              padding: '10px 20px',
              background: '#96ca2d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? '10px' : '20px',
            right: isMobile ? '10px' : '20px',
            left: isMobile ? '10px' : 'auto',
            zIndex: 10001,
            minWidth: isMobile ? 'auto' : '300px',
            maxWidth: isMobile ? 'none' : '500px',
            width: isMobile ? 'calc(100% - 20px)' : 'auto',
            background: notification.type === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            padding: isMobile ? '12px 16px' : '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer'
          }}
          onClick={() => setNotification(null)}
        >
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
          <span style={{ flex: 1 }}>{notification.message}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setNotification(null);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              fontSize: '16px'
            }}
          >
            <i className="fas fa-times" />
          </button>
        </div>
      )}
      
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '16px' : '24px',
        color: 'white',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          flexWrap: 'wrap', 
          gap: isMobile ? '16px' : '20px',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ 
            flex: 1, 
            width: isMobile ? '100%' : 'auto',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <h1 style={{ margin: 0, fontSize: isMobile ? '22px' : '28px', fontWeight: 700 }}>Deliverables Management</h1>
            <p style={{ margin: '8px 0 0 0', opacity: 0.95, fontSize: isMobile ? '14px' : '16px' }}>Manage project deliverables</p>
            <div style={{ 
              display: 'flex', 
              gap: isMobile ? '12px' : '16px', 
              marginTop: isMobile ? '16px' : '24px', 
              flexWrap: 'wrap',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: isMobile ? '8px' : '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                padding: isMobile ? '10px 16px' : '12px 20px',
                borderRadius: '12px',
                flex: isMobile ? '1' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}>
                <i className="fas fa-file-alt" style={{ fontSize: isMobile ? '18px' : '20px' }} />
                <div>
                  <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700 }}>{deliverables.length}</div>
                  <div style={{ fontSize: isMobile ? '11px' : '13px', opacity: 0.85 }}>Total Deliverables</div>
                </div>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: isMobile ? '8px' : '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                padding: isMobile ? '10px 16px' : '12px 20px',
                borderRadius: '12px',
                flex: isMobile ? '1' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}>
                <i className="fas fa-check-circle" style={{ fontSize: isMobile ? '18px' : '20px' }} />
                <div>
                  <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700 }}>
                    {deliverables.filter(d => d.status === 'completed').length}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '13px', opacity: 0.85 }}>Completed Deliverables</div>
                </div>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: isMobile ? '8px' : '12px',
                background: 'rgba(255, 255, 255, 0.15)',
                padding: isMobile ? '10px 16px' : '12px 20px',
                borderRadius: '12px',
                flex: isMobile ? '1' : 'none',
                minWidth: isMobile ? '0' : 'auto'
              }}>
                <i className="fas fa-project-diagram" style={{ fontSize: isMobile ? '18px' : '20px' }} />
                <div>
                  <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700 }}>
                    {(() => {
                      const uniqueWps = new Set(deliverables.filter(d => d.wp).map(d => d.wp));
                      return uniqueWps.size;
                    })()}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '13px', opacity: 0.85 }}>Work Packages with Deliverables</div>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingDeliverable(null);
              setShowDeliverableForm(true);
            }}
            style={{
              padding: isMobile ? '10px 20px' : '12px 24px',
              background: 'rgba(255,255,255,0.2)',
              border: '2px solid white',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 600,
              transition: 'all 0.3s ease',
              width: isMobile ? '100%' : 'auto'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#96ca2d';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              e.currentTarget.style.color = 'white';
            }}
          >
            + New Deliverable
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: 'white',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '16px' : '20px',
        marginBottom: isMobile ? '16px' : '24px',
        border: '1px solid rgba(150, 202, 45, 0.1)',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '12px' : '16px',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        <div style={{ flex: '1', minWidth: isMobile ? '100%' : '250px', width: isMobile ? '100%' : 'auto', maxWidth: '100%', boxSizing: 'border-box' }}>
          <div style={{ position: 'relative', width: '100%' }}>
            <i className="fas fa-search" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '14px',
              zIndex: 1
            }} />
            <input
              type="text"
              placeholder="Search by ID or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                maxWidth: '100%',
                padding: '10px 12px 10px 36px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
        <div style={{ minWidth: isMobile ? '100%' : '180px', width: isMobile ? '100%' : 'auto', flex: isMobile ? '1' : 'none', maxWidth: '100%', boxSizing: 'border-box' }}>
          <select
            value={wpFilter}
            onChange={(e) => setWpFilter(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '100%',
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: 'white',
              boxSizing: 'border-box'
            }}
          >
            <option value="">All Work Packages</option>
            {workPackageIdsFromDeliverables && workPackageIdsFromDeliverables.length > 0 ? (
              workPackageIdsFromDeliverables
                .sort((a, b) => a.localeCompare(b))
                .map(wpId => {
                  const wp = workPackages.find(w => w.id === wpId);
                  return (
                    <option key={wpId} value={wpId}>
                      {wpId}
                    </option>
                  );
                })
            ) : (
              <option value="" disabled>No work packages with deliverables</option>
            )}
          </select>
        </div>
        <div style={{ minWidth: isMobile ? '100%' : '150px', width: isMobile ? '100%' : 'auto', flex: isMobile ? '1' : 'none', maxWidth: '100%', boxSizing: 'border-box' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '100%',
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: 'white',
              boxSizing: 'border-box'
            }}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        {(searchTerm || wpFilter || statusFilter) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setWpFilter('');
              setStatusFilter('');
            }}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(150, 202, 45, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(150, 202, 45, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(150, 202, 45, 0.2)';
            }}
          >
            Reset
          </button>
        )}
      </div>

      {/* Work Packages with Deliverables */}
      {(() => {
        // Grouper les deliverables par work package
        const deliverablesByWp = {};
        deliverables.forEach(del => {
          if (del.wp) {
            if (!deliverablesByWp[del.wp]) {
              deliverablesByWp[del.wp] = [];
            }
            deliverablesByWp[del.wp].push(del);
          }
        });

        const wpsWithDeliverables = Object.keys(deliverablesByWp)
          .map(wpId => {
            const wp = workPackages.find(w => w.id === wpId);
            return {
              wpId,
              wp: wp || { id: wpId, title: `Work Package ${wpId}` }, // Fallback si wp non trouvé
              deliverables: deliverablesByWp[wpId],
              count: deliverablesByWp[wpId].length
            };
          })
          .sort((a, b) => a.wpId.localeCompare(b.wpId)); // Trier par ID

        if (wpsWithDeliverables.length === 0) return null;

        return (
          <div style={{
            background: 'white',
            borderRadius: isMobile ? '12px' : '16px',
            padding: isMobile ? '16px' : '24px',
            marginBottom: isMobile ? '16px' : '24px',
            border: '1px solid rgba(150, 202, 45, 0.1)',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden',
            overflowY: 'visible'
          }}>
            <h2 style={{
              margin: '0 0 20px 0',
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 700,
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <i className="fas fa-project-diagram" style={{ color: '#96ca2d' }} />
              Work Packages with Deliverables
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: isMobile ? '12px' : '16px',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'hidden'
            }}>
              {wpsWithDeliverables.map(({ wpId, wp, deliverables: wpDeliverables, count }) => (
                <div
                  key={wpId}
                  style={{
                    background: '#f9fafb',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '16px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#96ca2d';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(150, 202, 45, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onClick={() => {
                    setWpFilter(wpId);
                    // Scroll to deliverables list
                    setTimeout(() => {
                      const deliverablesList = document.querySelector('[data-deliverables-list]');
                      if (deliverablesList) {
                        deliverablesList.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    marginBottom: '12px',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <div style={{ 
                      flex: 1,
                      minWidth: 0,
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        fontWeight: 600,
                        marginBottom: '4px',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {wpId}
                      </div>
                      <h3 style={{
                        margin: 0,
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: '#1f2937',
                        lineHeight: '1.4',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%'
                      }}>
                        {wp.title || 'Untitled Work Package'}
                      </h3>
                    </div>
                    <div style={{
                      background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      marginLeft: '12px'
                    }}>
                      {count} {count === 1 ? 'Deliverable' : 'Deliverables'}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#6b7280',
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #e5e7eb',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    overflowX: 'hidden'
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>Recent Deliverables:</div>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '4px',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}>
                      {wpDeliverables.slice(0, 3).map(del => (
                        <div key={del.id || del.mongoId} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '0.8rem',
                          width: '100%',
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          minWidth: 0
                        }}>
                          <i className="fas fa-circle" style={{ fontSize: '4px', color: '#96ca2d', flexShrink: 0 }} />
                          <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: 1
                          }}>
                            {del.id}: {del.description ? (del.description.length > 40 ? del.description.substring(0, 40) + '...' : del.description) : 'No description'}
                          </span>
                        </div>
                      ))}
                      {wpDeliverables.length > 3 && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic', marginTop: '4px' }}>
                          +{wpDeliverables.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Deliverables List */}
      <div 
        data-deliverables-list
        style={{
          background: 'white',
          borderRadius: isMobile ? '12px' : '16px',
          padding: isMobile ? '16px' : '24px',
          border: '1px solid rgba(150, 202, 45, 0.1)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
        {currentDeliverables.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            <p>No deliverables found.</p>
            <button
              onClick={() => {
                setEditingDeliverable(null);
                setShowDeliverableForm(true);
              }}
              style={{
                padding: '12px 24px',
                background: '#96ca2d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginTop: '16px'
              }}
            >
              Create First Deliverable
            </button>
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: isMobile ? '16px' : '20px',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
              {currentDeliverables.map(del => {
                const getStatusColor = (status) => {
                  switch (status) {
                    case 'completed':
                      return '#10b981';
                    case 'in_progress':
                      return '#f59e0b';
                    case 'pending':
                    default:
                      return '#6b7280';
                  }
                };

                const getStatusLabel = (status) => {
                  switch (status) {
                    case 'completed':
                      return 'Completed';
                    case 'in_progress':
                      return 'In Progress';
                    case 'pending':
                    default:
                      return 'Pending';
                  }
                };

                return (
                  <div key={del.id || del.mongoId} style={{
                    background: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: isMobile ? '10px' : '12px',
                    padding: isMobile ? '16px' : '20px',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#96ca2d';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    {/* Top gradient bar */}
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '0',
                      right: '0',
                      height: '4px',
                      background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)'
                    }} />
                    
                    {/* Card Content */}
                    <div style={{ paddingTop: '4px' }}>
                      {/* ID and Status */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{
                          background: '#f8f9fa',
                          color: '#6c757d',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          border: '1px solid #e9ecef'
                        }}>
                          {del.id}
                        </span>
                        <span style={{
                          background: getStatusColor(del.status),
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {getStatusLabel(del.status)}
                        </span>
                      </div>

                      {/* Description */}
                      <h3 style={{
                        margin: '0 0 12px 0',
                        fontSize: isMobile ? '1rem' : '1.1rem',
                        fontWeight: '600',
                        color: '#1f2937',
                        lineHeight: '1.4',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%'
                      }}>
                        {del.description || 'No description'}
                      </h3>

                      {/* Work Package */}
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: 600 }}>
                          Work Package
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#1f2937', fontWeight: 500 }}>
                          {(() => {
                            if (!del.wp) return 'N/A';
                            const wp = workPackages.find(w => w.id === del.wp);
                            if (wp && wp.title) {
                              return `${wp.id}: ${wp.title}`;
                            }
                            return del.wp;
                          })()}
                        </div>
                      </div>

                      {/* Details */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', fontSize: '0.85rem', color: '#6b7280' }}>
                        {del.date && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fas fa-calendar-alt" style={{ color: '#96ca2d', fontSize: '0.75rem' }} />
                            <span>Date: {del.date}</span>
                          </div>
                        )}
                        {del.lead && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fas fa-user" style={{ color: '#96ca2d', fontSize: '0.75rem' }} />
                            <span>Lead: {del.lead}</span>
                          </div>
                        )}
                        {del.file && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fas fa-file" style={{ color: '#96ca2d', fontSize: '0.75rem' }} />
                            <span>File available</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: isMobile ? '6px' : '8px', justifyContent: 'flex-end', marginTop: 'auto', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                        <button
                          onClick={() => {
                            setEditingDeliverable(del);
                            setShowDeliverableForm(true);
                          }}
                          style={{
                            padding: isMobile ? '6px 12px' : '8px 16px',
                            background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: isMobile ? '12px' : '13px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(150, 202, 45, 0.2)',
                            flex: isMobile ? '1' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(150, 202, 45, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(150, 202, 45, 0.2)';
                          }}
                        >
                          <i className="fas fa-edit" style={{ marginRight: '6px' }} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDeliverable(del.id || del.mongoId)}
                          style={{
                            padding: isMobile ? '6px 12px' : '8px 16px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: isMobile ? '12px' : '13px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)',
                            flex: isMobile ? '1' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.2)';
                          }}
                        >
                          <i className="fas fa-trash" style={{ marginRight: '6px' }} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {filteredDeliverables.length > itemsPerPage && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: isMobile ? '4px' : '8px',
                marginTop: isMobile ? '20px' : '30px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #96ca2d',
                    background: currentPage === 1 ? '#f2f2f2' : '#96ca2d',
                    color: currentPage === 1 ? '#888' : '#fff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: isMobile ? '12px' : '14px'
                  }}
                >&lt;</button>
                {Array.from({ length: Math.min(totalPages, isMobile ? 3 : 5) }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      padding: isMobile ? '6px 10px' : '8px 12px',
                      borderRadius: '6px',
                      border: `1px solid ${currentPage === page ? '#96ca2d' : '#ddd'}`,
                      background: currentPage === page ? '#96ca2d' : '#fff',
                      color: currentPage === page ? '#fff' : '#333',
                      cursor: 'pointer',
                      fontSize: isMobile ? '12px' : '14px'
                    }}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #96ca2d',
                    background: currentPage === totalPages ? '#f2f2f2' : '#96ca2d',
                    fontSize: isMobile ? '12px' : '14px',
                    color: currentPage === totalPages ? '#888' : '#fff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >&gt;</button>
              </div>
            )}
          </>
        )}
      </div>
      </div>

      {/* Deliverable Form Modal */}
      {showDeliverableForm && (
        <DeliverableForm
          deliverable={editingDeliverable}
          workPackages={workPackages}
          workPackageIdsFromDeliverables={workPackageIdsFromDeliverables}
          onClose={() => {
            setShowDeliverableForm(false);
            setEditingDeliverable(null);
          }}
          onSubmit={handleDeliverableSubmit}
          isMobile={isMobile}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deliverableToDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '20px'
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: isMobile ? '12px' : '16px',
              maxWidth: isMobile ? '100%' : '450px',
              width: '100%',
              padding: isMobile ? '20px' : '24px'
            }}
          >
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <i className="fas fa-exclamation-triangle" style={{
                fontSize: '48px',
                color: '#ef4444',
                marginBottom: '16px'
              }} />
              <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px', fontWeight: 700 }}>
                Delete Deliverable
              </h2>
              <p style={{ margin: '12px 0 0 0', color: '#6b7280', fontSize: isMobile ? '13px' : '14px' }}>
                Are you sure you want to delete "{deliverableToDelete.id}: {deliverableToDelete.description}"? This action cannot be undone.
              </p>
            </div>
            <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', justifyContent: 'flex-end', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <button
                onClick={() => setDeliverableToDelete(null)}
                style={{
                  padding: isMobile ? '8px 16px' : '10px 20px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  background: '#ffffff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  flex: isMobile ? '1' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#96ca2d';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteDeliverable}
                style={{
                  padding: isMobile ? '8px 16px' : '10px 20px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: 600,
                  flex: isMobile ? '1' : 'none'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Deliverable Form Component
const DeliverableForm = ({ deliverable, workPackages, workPackageIdsFromDeliverables, onClose, onSubmit, isMobile: propIsMobile }) => {
  const [isMobile, setIsMobile] = useState(propIsMobile || false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [formData, setFormData] = useState({
    id: deliverable?.id || '',
    wp: deliverable?.wp || '',
    description: deliverable?.description || '',
    status: deliverable?.status || 'pending',
    file: deliverable?.file || '',
    fileType: deliverable?.file ? (deliverable.file.startsWith('http://') || deliverable.file.startsWith('https://') ? 'link' : 'upload') : 'link',
    date: deliverable?.date || '',
    lead: deliverable?.lead || ''
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileError, setFileError] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFileError(null);
      return;
    }

    // Réinitialiser l'erreur précédente
    setFileError(null);

    // Vérifier le type de fichier (PDF ou DOCX)
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const allowedExtensions = ['.pdf', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setFileError('Please upload a PDF or DOCX file only.');
      e.target.value = '';
      setUploadedFile(null);
      return;
    }

    // Vérifier la taille du fichier (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB en bytes
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setFileError(`File size exceeds the maximum allowed size of 50MB. Your file is ${fileSizeMB}MB.`);
      e.target.value = '';
      setUploadedFile(null);
      return;
    }

    setUploadedFile(file);
    setIsUploading(true);
    setFileError(null);

    try {
      const uploaded = await uploadResourceFile(file);
      if (uploaded && uploaded.path) {
        setFormData({ ...formData, file: uploaded.path, fileType: 'upload' });
        setFileError(null);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setFileError('Error uploading file. Please try again.');
      setUploadedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Si un fichier a été uploadé mais pas encore traité, attendre
    if (uploadedFile && isUploading) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: isMobile ? '10px' : '20px',
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          animation: 'fadeIn 0.3s ease-out'
        }}
        onClick={onClose}
      >
      <div
        style={{
          background: 'white',
          borderRadius: isMobile ? '16px' : '24px',
          maxWidth: isMobile ? 'calc(100% - 20px)' : '750px',
          width: '100%',
          maxHeight: isMobile ? '95vh' : '90vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
          boxShadow: '0 30px 100px rgba(0, 0, 0, 0.5)',
          boxSizing: 'border-box',
          animation: 'slideUp 0.3s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
          padding: isMobile ? '20px' : '28px',
          borderRadius: isMobile ? '16px 16px 0 0' : '24px 24px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(150, 202, 45, 0.2)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
            <h2 style={{ 
              margin: 0, 
              fontSize: isMobile ? '20px' : '26px', 
              fontWeight: 800, 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              letterSpacing: '-0.5px'
            }}>
              <i className={`fas ${deliverable ? 'fa-edit' : 'fa-plus-circle'}`} style={{ fontSize: isMobile ? '20px' : '24px' }} />
              {deliverable ? 'Edit' : 'New'} Deliverable
            </h2>
            <button
              onClick={onClose}
              style={{
                width: isMobile ? '36px' : '40px',
                height: isMobile ? '36px' : '40px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.25)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                cursor: 'pointer',
                color: 'white',
                fontSize: isMobile ? '16px' : '18px',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(90deg) scale(1.1)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) scale(1)';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
              }}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        <div style={{ 
          padding: isMobile ? '24px 16px' : '36px',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}>
          <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <div style={{ display: 'grid', gap: isMobile ? '24px' : '28px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                gap: isMobile ? '12px' : '16px',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 600, fontSize: '14px' }}>
                    <i className="fas fa-hashtag" style={{ color: '#96ca2d' }} />
                    Deliverable ID <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                    required
                    placeholder="e.g., D0.1"
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '14px 18px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '15px',
                      boxSizing: 'border-box',
                      transition: 'all 0.3s ease',
                      background: '#ffffff'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#96ca2d';
                      e.target.style.boxShadow = '0 0 0 4px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 600, fontSize: '14px' }}>
                    <i className="fas fa-project-diagram" style={{ color: '#96ca2d' }} />
                    Work Package <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    value={formData.wp}
                    onChange={(e) => setFormData({ ...formData, wp: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '14px 18px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '15px',
                      background: 'white',
                      boxSizing: 'border-box',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#96ca2d';
                      e.target.style.boxShadow = '0 0 0 4px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="">Select Work Package</option>
                    {workPackageIdsFromDeliverables && workPackageIdsFromDeliverables.length > 0
                      ? workPackageIdsFromDeliverables
                          .sort((a, b) => a.localeCompare(b))
                          .map(wpId => (
                            <option key={wpId} value={wpId}>
                              {wpId}
                            </option>
                          ))
                      : null}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 600, fontSize: '14px' }}>
                  <i className="fas fa-align-left" style={{ color: '#96ca2d' }} />
                  Description <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="Deliverable description"
                  rows={4}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '14px 18px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '15px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    background: '#ffffff',
                    lineHeight: '1.5'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#96ca2d';
                    e.target.style.boxShadow = '0 0 0 4px rgba(150, 202, 45, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '12px' : '16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 600, fontSize: '14px' }}>
                    <i className="fas fa-info-circle" style={{ color: '#96ca2d' }} />
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '14px 18px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '15px',
                      background: 'white',
                      boxSizing: 'border-box',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#96ca2d';
                      e.target.style.boxShadow = '0 0 0 4px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 600, fontSize: '14px' }}>
                    <i className="fas fa-calendar" style={{ color: '#96ca2d' }} />
                    Date
                  </label>
                  <input
                    type="text"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="e.g., M6, M12"
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '14px 18px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '15px',
                      boxSizing: 'border-box',
                      transition: 'all 0.3s ease',
                      background: '#ffffff'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#96ca2d';
                      e.target.style.boxShadow = '0 0 0 4px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 600, fontSize: '14px' }}>
                  <i className="fas fa-user" style={{ color: '#96ca2d' }} />
                  Lead
                </label>
                <input
                  type="text"
                  value={formData.lead}
                  onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                  placeholder="e.g., ERU (Türkiye)"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '14px 18px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '15px',
                    boxSizing: 'border-box',
                    transition: 'all 0.3s ease',
                    background: '#ffffff'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#96ca2d';
                    e.target.style.boxShadow = '0 0 0 4px rgba(150, 202, 45, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontWeight: 600, fontSize: '14px' }}>
                  <i className="fas fa-file" style={{ color: '#96ca2d' }} />
                  File (optional)
                </label>
                
                {/* Toggle entre Link et Upload */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, fileType: 'link', file: '' });
                        setUploadedFile(null);
                        setFileError(null);
                      }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: `2px solid ${formData.fileType === 'link' ? '#96ca2d' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      background: formData.fileType === 'link' ? '#f0f9ff' : 'white',
                      color: formData.fileType === 'link' ? '#96ca2d' : '#6b7280',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: formData.fileType === 'link' ? 600 : 500,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <i className="fas fa-link" style={{ marginRight: '6px' }} />
                    Link
                  </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, fileType: 'upload', file: '' });
                        setUploadedFile(null);
                        setFileError(null);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: `2px solid ${formData.fileType === 'upload' ? '#96ca2d' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        background: formData.fileType === 'upload' ? '#f0f9ff' : 'white',
                        color: formData.fileType === 'upload' ? '#96ca2d' : '#6b7280',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: formData.fileType === 'upload' ? 600 : 500,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="fas fa-upload" style={{ marginRight: '6px' }} />
                      Upload PDF/DOCX
                    </button>
                </div>

                {/* Input selon le type choisi */}
                {formData.fileType === 'link' ? (
                  <input
                    type="text"
                    value={formData.file || ''}
                    onChange={(e) => setFormData({ ...formData, file: e.target.value })}
                    placeholder="https://example.com/file.pdf or /uploads/file.pdf"
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: '14px 18px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '15px',
                      boxSizing: 'border-box',
                      transition: 'all 0.3s ease',
                      background: '#ffffff'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#96ca2d';
                      e.target.style.boxShadow = '0 0 0 4px rgba(150, 202, 45, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                ) : (
                  <div>
                    <input
                      type="file"
                      accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFileChange}
                      disabled={isUploading}
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '15px',
                        cursor: isUploading ? 'not-allowed' : 'pointer',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                      Maximum file size: 50MB
                    </div>
                    {fileError && (
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fas fa-exclamation-circle" />
                        {fileError}
                      </div>
                    )}
                    {isUploading && (
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#96ca2d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fas fa-spinner fa-spin" />
                        Uploading...
                      </div>
                    )}
                    {uploadedFile && !isUploading && !fileError && (
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fas fa-check-circle" />
                        {uploadedFile.name}
                      </div>
                    )}
                    {formData.file && formData.fileType === 'upload' && !fileError && (
                      <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
                        Current file: {formData.file}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                gap: isMobile ? '10px' : '14px', 
                justifyContent: isMobile ? 'stretch' : 'flex-end', 
                marginTop: '36px', 
                paddingTop: '24px', 
                borderTop: '2px solid #f3f4f6',
                flexWrap: isMobile ? 'wrap' : 'nowrap'
              }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: isMobile ? '14px 24px' : '16px 32px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    background: '#ffffff',
                    color: '#374151',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    flex: isMobile ? '1' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#96ca2d';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <i className="fas fa-times" />
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: isMobile ? '14px 24px' : '16px 32px',
                    background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(150, 202, 45, 0.3)',
                    transition: 'all 0.3s ease',
                    flex: isMobile ? '1' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(150, 202, 45, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(150, 202, 45, 0.3)';
                  }}
                >
                  <i className={`fas ${deliverable ? 'fa-save' : 'fa-plus-circle'}`} />
                  {deliverable ? 'Update' : 'Create'} Deliverable
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
    <style>{`
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `}</style>
    </>
  );
};

export default Deliverables;

